import {
	contentControllerGetLibraryArchivedContentByPage,
	contentControllerGetLibraryContentByPage,
	contentControllerGetLibraryTempContent,
	ContentItemCreationMode,
	type ContentControllerGetLibraryContentByPageCreationMode,
	type GetLibraryArchivedContentDtoRes,
	type GetLibraryContentDtoRes,
} from "@/shared/api/generated"
import { QUERY_KEYS } from "@/shared/constants"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useContentCacheStore } from "./store"

// import * as FileSystem from "expo-file-system";
// import { useAudioStore } from "../audio/store";



export function useContentCache() {
	const queryClient = useQueryClient()



	/** Invalidates every library page (all tabs / filters). When the last
	 * item of the currently viewed page is being removed, steps that tab's
	 * page back first so the user doesn't land on an empty page. */
	const invalidateLibraryActivePage = async (isRemovingFromPage?: boolean) => {
		const state = useContentCacheStore.getState()
		const tabs: { page: number; creationMode?: string; setPage: (page: number) => void }[] = [
			{ page: state.activeAllPage, setPage: state.setActiveAllPage },
			{
				page: state.activeExtensionPage,
				creationMode: ContentItemCreationMode.SELECTION,
				setPage: state.setActiveExtensionPage,
			},
		]

		if (isRemovingFromPage) {
			for (const tab of tabs) {
				const queryDataForActivePage = queryClient.getQueryData(
					QUERY_KEYS.LIBRARY_CONTENT(tab.page, tab.creationMode)
				) as GetLibraryContentDtoRes | undefined
				if (queryDataForActivePage && queryDataForActivePage.data.length === 1 && tab.page > 1) {
					tab.setPage(tab.page - 1)
				}
			}
		}

		await queryClient.invalidateQueries({ queryKey: ["LIBRARY_CONTENT"] })
	}

	const invalidateLibraryArchivedPage = async (isRemovingFromPage?: boolean) => {


		const activePage = useContentCacheStore.getState().activeArchivedPage;
		const queryDataForActivePage = queryClient.getQueryData(QUERY_KEYS.ARCHIVED_CONTENT(activePage)) as GetLibraryArchivedContentDtoRes
		if (queryDataForActivePage && queryDataForActivePage.data.length === 1 && isRemovingFromPage && activePage > 1) {
			useContentCacheStore.getState().setActiveArchivedPage(activePage - 1)
			await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ARCHIVED_CONTENT(activePage - 1) })
			return
		}
		await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ARCHIVED_CONTENT(activePage) })
	}


	const invalidateTempContent = async () => {
		await queryClient.invalidateQueries({
			queryKey: QUERY_KEYS.TEMP_CONTENT,
		});
	};

	const clear = () => {
		queryClient.removeQueries({ queryKey: ["LIBRARY_CONTENT"] })
		queryClient.removeQueries({ queryKey: ["ARCHIVED_CONTENT"] })
		queryClient.removeQueries({ queryKey: ["TEMP_CONTENT"] })
		queryClient.removeQueries({ queryKey: ["CONTENT_BY_ID"] })
	}

	return {
		invalidateLibraryActivePage,
		invalidateLibraryArchivedPage,
		invalidateTempContent,
		clear
	}
}


//

export function useGetLibraryContentByPage(
	activePage: number,
	creationMode?: ContentControllerGetLibraryContentByPageCreationMode
) {
	return useQuery({
		queryFn: () =>
			contentControllerGetLibraryContentByPage({
				page: activePage.toString(),
				platform: "web",
				...(creationMode ? { creationMode } : {}),
			}),
		queryKey: QUERY_KEYS.LIBRARY_CONTENT(activePage, creationMode),
		placeholderData: previousData => previousData,
		// Never rely on cache: always refetch, and discard cached data as soon as it's unused.
		staleTime: 0,
		gcTime: 0,
		refetchOnMount: "always",
		refetchOnReconnect: "always",
	});
}

export function useGetLibraryArchivedContentByPage(activePage: number) {
	return useQuery({
		queryFn: () =>
			contentControllerGetLibraryArchivedContentByPage({
				page: activePage.toString(),
				platform: "web",
			}),
		queryKey: QUERY_KEYS.ARCHIVED_CONTENT(activePage),
		placeholderData: previousData => previousData,
		// Never rely on cache: always refetch, and discard cached data as soon as it's unused.
		staleTime: 0,
		gcTime: 0,
		refetchOnMount: "always",
		refetchOnReconnect: "always",
	});
}


export function useGetLibraryTempContent() {
	return useQuery({
		queryFn: contentControllerGetLibraryTempContent,
		queryKey: QUERY_KEYS.TEMP_CONTENT,
		refetchOnMount: "always",
		refetchOnReconnect: "always",
		staleTime: 0,
		gcTime: 0,
		refetchInterval: 1000 * 60 * 5 // 5 minutes
	});
}